import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import SubjectFilterSelect from "../../../containers/admin/shared/subject-filter-select";
import { useSuperAdminSubjectFilter } from "../../../helpers/useSuperAdminSubjectFilter";
import { getObsoleteNumbersRequest } from "../../../modules/admin-modules/reports/Actions";

const LISTS = [
  {
    key: "parentLikePhones",
    label: "رقم الطالب يطابق ولي الأمر",
    sheet: "رقم الطالب يطابق ولي الأمر",
  },
  {
    key: "phonesLessThan14Digits",
    label: "رقم غير صالح",
    sheet: "رقم غير صالح",
  },
  {
    key: "phonesInWaEvents",
    label: "رسائل واتساب لا تصل",
    sheet: "رسائل واتساب لا تصل",
  },
];

const columns = [
  { Header: "#", accessor: "index" },
  { Header: "id", accessor: "id" },
  { Header: "Code", accessor: "user_name" },
  { Header: "الاسم", accessor: "name" },
  { Header: "رقم الطالب", accessor: "phone" },
  { Header: "رقم ولي الأمر", accessor: "parent_phone" },
];

const mapRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row, index) => ({
    index: index + 1,
    id: row?.id ?? "",
    user_name: row?.user_name ?? "",
    name: row?.name ?? "",
    phone: row?.phone ?? "",
    parent_phone: row?.parent_phone ?? "",
  }));

const excelPhone = (value) => {
  if (value == null || value === "") return "";
  return `\u200B${String(value)}`;
};

const toSheetRows = (rows) =>
  mapRows(rows).map((row) => ({
    "#": row.index,
    Code: row.user_name,
    id: row.id,
    الاسم: row.name,
    "رقم الطالب": excelPhone(row.phone),
    "رقم ولي الأمر": excelPhone(row.parent_phone),
  }));

const PhoneListTable = ({ rows, isLoading }) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(15);
  const Data = useMemo(() => mapRows(rows), [rows]);

  useEffect(() => {
    setPageNumber(0);
  }, [rows]);

  return (
    <CTable
      selectedData={() => {}}
      Columns={columns}
      Data={Data}
      path={"openModel"}
      Actions
      Title=""
      subTitle=""
      btnTitle=""
      placeHolder=""
      noSearchBar={true}
      noFilter={true}
      footerBtnTitle
      filterList
      addButton
      addButtonTitle=""
      setPage={() => {}}
      setPerPage={setPerPage}
      currentpage={pageNumber}
      setPageNumber={setPageNumber}
      noSecondSearchBar={true}
      isLoading={isLoading}
      perPage={size}
      totalPage={1}
    />
  );
};

const ObsoleteNumbersReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const isUserLoading = useSelector((state) => state.user.isLoading);
  const obsoleteNumbers = useSelector((state) => state.reports.obsoleteNumbers);
  const isLoading = useSelector((state) => state.reports.isLoading);
  const subjects = useSelector((state) => state.subjects.subjects);
  const { isSuperAdmin, selectedSubjectId, setSelectedSubjectId } =
    useSuperAdminSubjectFilter();

  useEffect(() => {
    if (isUserLoading) return;
    if (user?.role && Array.isArray(user.role) && !isSuperAdmin) {
      navigate("/dashboard/students");
    }
  }, [user, isUserLoading, isSuperAdmin, navigate]);

  if (isUserLoading || !user?.role || !isSuperAdmin) {
    return null;
  }

  const lists = {
    parentLikePhones: obsoleteNumbers?.parentLikePhones || [],
    phonesLessThan14Digits: obsoleteNumbers?.phonesLessThan14Digits || [],
    phonesInWaEvents: obsoleteNumbers?.phonesInWaEvents || [],
  };
  const totalCount = LISTS.reduce((sum, list) => sum + lists[list.key].length, 0);

  const handleFetch = () => {
    if (!selectedSubjectId) {
      toast.error("اختر المادة أولاً");
      return;
    }
    dispatch(getObsoleteNumbersRequest({ subject_id: selectedSubjectId }));
  };

  const exportToExcel = () => {
    if (!totalCount) {
      toast.error("لا توجد بيانات للتصدير");
      return;
    }

    const workbook = XLSX.utils.book_new();
    LISTS.forEach((list) => {
      const sheetRows = toSheetRows(lists[list.key]);
      const worksheet = XLSX.utils.json_to_sheet(
        sheetRows.length
          ? sheetRows
          : [{ "#": "", الكود: "", الاسم: "", "رقم الطالب": "", "رقم ولي الأمر": "" }]
      );
      worksheet["!cols"] = [
        { width: 8 },
        { width: 12 },
        { width: 28 },
        { width: 22 },
        { width: 22 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, list.sheet);
    });

    const subjectName =
      subjects?.find((subject) => String(subject.id) === String(selectedSubjectId))
        ?.name || "subject";
    const safeName = String(subjectName).replace(/[\\/:*?"<>|]/g, "-");
    XLSX.writeFile(
      workbook,
      `obsolete-numbers-${safeName}-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10000000"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            أرقام غير صالحة
          </Text>
          <Flex alignItems="center" gap={3} flexWrap="wrap">
            <Button
              bg="main_1"
              color="white"
              onClick={exportToExcel}
              isDisabled={!totalCount}
            >
              تصدير Excel
            </Button>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box mb={6} p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="16px" fontWeight="500" mb={4}>
            اختر المادة
          </Text>
          <Flex gap={4} alignItems="center" flexWrap="wrap">
            <Box minW={{ base: "100%", md: "320px" }} flex="1">
              <SubjectFilterSelect
                selectedSubjectId={selectedSubjectId}
                setSelectedSubjectId={setSelectedSubjectId}
                emptyHint="اختر المادة لعرض الأرقام غير الصالحة"
              />
            </Box>
            <Button
              bg="main_1"
              color="white"
              onClick={handleFetch}
              isLoading={isLoading}
              isDisabled={!selectedSubjectId}
            >
              عرض التقرير
            </Button>
          </Flex>
        </Box>

        <Tabs colorScheme="red" variant="enclosed" isLazy>
          <TabList flexWrap="wrap">
            {LISTS.map((list) => (
              <Tab key={list.key}>
                {list.label} ({lists[list.key].length})
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {LISTS.map((list) => (
              <TabPanel key={list.key} px={0}>
                <PhoneListTable rows={lists[list.key]} isLoading={isLoading} />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ObsoleteNumbersReport;
